import { Component, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WizardPageComponent } from 'src/app/core/wizard/wizard-page-component';
import { RankByStoreWizard } from '../rank-by-store-wizard';

export interface RankingData {
  rank: number;
  locationName: string;
  address: string;
  annualSpend: string;
  isSelected?: boolean;
  isClickedStore?: boolean;
}

@Component({
  selector: 'mtn-rank-detail-wizard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './rank-detail-wizard.component.html',
  styleUrl: './rank-detail-wizard.component.scss'
})
export class RankDetailWizardComponent extends WizardPageComponent<RankByStoreWizard> {
  key = 'ranking-detail';

  industryRankingData: RankingData[] = [];
  bannerRankingData: RankingData[]   = [];
  hasBannerData = false;
  isLoading = false;

  rankingType = 'National';
  salesLabel  = 'Annual Sales';
  private clickedStoreUuid: string | undefined;

  activeTab: 'company' | 'industry' = 'company';

  get activeData(): RankingData[] {
    return this.activeTab === 'company' ? this.bannerRankingData : this.industryRankingData;
  }

  get activeCount(): number {
    return this.activeData.length;
  }

  switchTab(tab: 'company' | 'industry'): void {
    this.activeTab = tab;
  }

  constructor(private cdr: ChangeDetectorRef) {
    super();
    this.closeButtonText = 'Close';
  }

  onLoad() {
    const model = this.wizard.model;
    this.rankingType      = model?.rankingType      || 'National';
    this.salesLabel       = model?.salesLabel       || 'Annual Sales';
    this.clickedStoreUuid = model?.clickedStoreUuid || undefined;
    this.title = `${this.rankingType} Ranking`;

    if (model?.dataPromise) {
      this.isLoading = true;
      (model.dataPromise as Promise<any[]>)
        .then((allRows: any[]) => {
          this.processRows(allRows);
          this.isLoading = false;
          this.cdr.markForCheck();
        })
        .catch(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        });
    } else {
      this.processRows(model?.tableData || []);
    }

    return super.onLoad();
  }

  private processRows(allRows: any[]): void {
    this.industryRankingData = this.buildRows(allRows, this.rankingType, 'industry');
    this.bannerRankingData   = this.buildRows(allRows, this.rankingType, 'banner');
    this.hasBannerData       = this.bannerRankingData.length > 0;
    this.activeTab = this.hasBannerData ? 'company' : 'industry';
  }

  /**
   * Returns the correct rank value from a row for the given rankingType and scope.
   * scope = 'industry' → use industry fields (national_rank_industry etc.)
   * scope = 'banner'   → use banner fields (national_rank_banner etc.)
   */
  private getRankValue(row: any, rankingType: string, scope: 'industry' | 'banner'): number {
    if (scope === 'industry') {
      // Industry fields are stored with 'industry' prefix in RankingTableData
      switch (rankingType) {
        case 'State':   return row.industryStateRank   ?? 0;
        case 'County':  return row.industryCountyRank  ?? 0;
        case 'MSA':     return row.industryMsaRank     ?? 0;
        case 'Miles':   return row.industryMiles       ?? 0;
        case 'Minutes': return row.industryMinutes     ?? 0;
        default:        return row.industryNationalRank ?? 0;
      }
    } else {
      // Banner fields are the primary fields in RankingTableData (shown in table)
      switch (rankingType) {
        case 'State':   return row.stateRank   ?? 0;
        case 'County':  return row.countyRank  ?? 0;
        case 'MSA':     return row.msaRank     ?? 0;
        case 'Miles':   return row.miles       ?? 0;
        case 'Minutes': return row.minutes     ?? 0;
        default:        return row.nationalRank ?? 0;
      }
    }
  }

  private buildRows(rows: any[], rankingType: string, scope: 'industry' | 'banner'): RankingData[] {
    return [...rows]
      .map(row => ({ row, rank: this.getRankValue(row, rankingType, scope) }))
      .filter(({ rank }) => rank > 0)
      .sort((a, b) => a.rank - b.rank)
      .map(({ row, rank }) => ({
        rank,
        locationName:   row.name       || 'Unknown',
        address:        row.address    || '',
        annualSpend:    row.annualSales || 'N/A',
        isSelected:     !!row.isSelected,
        isClickedStore: !!(this.clickedStoreUuid && row.storeUuid === this.clickedStoreUuid),
      }));
  }
}
